import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { AuthProvider } from '@/lib/auth'
import { Toaster } from '@/components/ui/toaster'

// Debug logging for E2E troubleshooting (TD-050)
console.log('[SPECTREIQ] App.tsx module loaded')

// Layout
import { AppLayout } from '@/components/layout/AppLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'

// Pages - Platform Level
import { LandingPage } from '@/pages/LandingPage'
import { LoginPage } from '@/pages/LoginPage'
import { PlatformDashboardPage } from '@/pages/PlatformDashboardPage'
import { MissionsPage } from '@/pages/MissionsPage'
import { MissionDetailPage } from '@/pages/MissionDetailPage'

// Pages - SoftwareIQ Module
import { SoftwareIQDashboardPage } from '@/pages/SoftwareIQDashboardPage'
import { SoftwareAssurancePage } from '@/pages/SoftwareAssurancePage'
import { SoftwareAssuranceWorkflowPage } from '@/pages/SoftwareAssuranceWorkflowPage'
import { FindingsPage } from '@/pages/FindingsPage'
import { FindingDetailPage } from '@/pages/FindingDetailPage'
import { VettingPage } from '@/pages/VettingPage'
import { SchemasPage } from '@/pages/SchemasPage'
import { StigsPage } from '@/pages/StigsPage'
import { ProcessDefinitionsPage } from '@/pages/ProcessDefinitionsPage'
import { MetadataIntegrationsPage } from '@/pages/MetadataIntegrationsPage'

// Pages - ComplyIQ Module
import { ComplyIQDashboardPage } from '@/pages/ComplyIQDashboardPage'
import { ComplianceManagementPage } from '@/pages/ComplianceManagementPage'
import { AuditManagementPage } from '@/pages/AuditManagementPage'
import { ControlsPage } from '@/pages/complyiq/ControlsPage'
import { AssessmentsPage } from '@/pages/complyiq/AssessmentsPage'
import { EvidencePage } from '@/pages/complyiq/EvidencePage'
import { AuditsPage } from '@/pages/complyiq/AuditsPage'
import { AuditFindingsPage } from '@/pages/complyiq/AuditFindingsPage'
import { EMASSistDashboardPage } from '@/pages/complyiq/EMASSistDashboardPage'
import { SSPRedactorPage } from '@/pages/complyiq/SSPRedactorPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public landing page - auth handled internally */}
        <Route path="/" element={<LandingPage />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Protected app routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Platform Level Routes */}
          <Route path="/dashboard" element={<PlatformDashboardPage />} />
          <Route path="/missions" element={<MissionsPage />} />
          <Route path="/missions/:id" element={<MissionDetailPage />} />
          <Route path="/metadataiq/processes" element={<ProcessDefinitionsPage />} />
          <Route path="/metadataiq/integrations" element={<MetadataIntegrationsPage />} />

          {/* SoftwareIQ Module Routes */}
          <Route path="/softwareiq" element={<SoftwareIQDashboardPage />} />

          {/* Software Assurance Workflow */}
          <Route path="/softwareiq/assurance" element={<SoftwareAssurancePage />} />
          <Route path="/softwareiq/assurance/workflow" element={<SoftwareAssuranceWorkflowPage />} />
          <Route path="/softwareiq/assurance/findings" element={<FindingsPage />} />
          <Route path="/softwareiq/assurance/findings/:findingId" element={<FindingDetailPage />} />
          <Route path="/softwareiq/assurance/vetting" element={<VettingPage />} />
          <Route path="/softwareiq/assurance/schemas" element={<SchemasPage />} />
          <Route path="/softwareiq/assurance/stigs" element={<StigsPage />} />

          {/* Legacy route redirects for backwards compatibility */}
          <Route path="/findings" element={<Navigate to="/softwareiq/assurance/findings" replace />} />
          <Route path="/vetting" element={<Navigate to="/softwareiq/assurance/vetting" replace />} />
          <Route path="/schemas" element={<Navigate to="/softwareiq/assurance/schemas" replace />} />
          <Route path="/stigs" element={<Navigate to="/softwareiq/assurance/stigs" replace />} />

          {/* ComplyIQ Module Routes */}
          <Route path="/complyiq" element={<ComplyIQDashboardPage />} />
          <Route path="/complyiq/emassist" element={<EMASSistDashboardPage />} />
          <Route path="/complyiq/ssp-redactor" element={<SSPRedactorPage />} />

          {/* Compliance Management Workflow */}
          <Route path="/complyiq/compliance" element={<ComplianceManagementPage />} />
          <Route path="/complyiq/compliance/controls" element={<ControlsPage />} />
          <Route path="/complyiq/compliance/assessments" element={<AssessmentsPage />} />
          <Route path="/complyiq/compliance/evidence" element={<EvidencePage />} />

          {/* Audit Management Workflow */}
          <Route path="/complyiq/audit" element={<AuditManagementPage />} />
          <Route path="/complyiq/audit/audits" element={<AuditsPage />} />
          <Route path="/complyiq/audit/findings" element={<AuditFindingsPage />} />

          {/* Future Module Placeholders */}
          <Route path="/supplyiq" element={<ComingSoonPage module="SupplyIQ" />} />
          <Route path="/projectiq" element={<ComingSoonPage module="ProjectIQ" />} />
          <Route path="/financialiq" element={<ComingSoonPage module="FinancialIQ" />} />
        </Route>

        {/* Fallback - send to landing page (will redirect to dashboard if authenticated) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </AuthProvider>
  )
}

// Simple coming soon placeholder for future modules
function ComingSoonPage({ module }: { module: string }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-4">{module}</h1>
        <p className="text-muted-foreground text-lg">
          This module is coming soon.
        </p>
      </div>
    </div>
  )
}
